/**
 * useEntityTags — batch-loads tags for a page of rows in ONE request and
 * exposes optimistic assign/unassign helpers.
 *
 * Usage:
 *   const { tagsByEntity, assign, unassign, refresh, loading } =
 *     useEntityTags('tracker_piping', visibleRowIds, { projectId });
 *   const rowTags = tagsByEntity[String(row.id)] || [];
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { lookupEntityTags, assignTag, unassignTag } from './tagApi';

const LOOKUP_CHUNK = 5000;

export default function useEntityTags(entityType, entityIds, { projectId = null, enabled = true } = {}) {
  const { instance, accounts } = useMsal();
  const [tagsByEntity, setTagsByEntity] = useState({});
  const [loading, setLoading] = useState(false);
  const loadSeq = useRef(0);

  // Stable key so we only refetch when the actual id set changes.
  const idsKey = useMemo(
    () => (Array.isArray(entityIds) ? entityIds.map(String).sort().join(',') : ''),
    [entityIds]
  );

  const refresh = useCallback(async () => {
    const ids = idsKey ? idsKey.split(',') : [];
    if (!enabled || !entityType || ids.length === 0) {
      setTagsByEntity({});
      return;
    }
    const seq = ++loadSeq.current;
    setLoading(true);
    try {
      const merged = {};
      for (let i = 0; i < ids.length; i += LOOKUP_CHUNK) {
        const chunk = ids.slice(i, i + LOOKUP_CHUNK);
        const map = await lookupEntityTags(instance, accounts, { entityType, entityIds: chunk });
        Object.assign(merged, map);
      }
      if (seq === loadSeq.current) setTagsByEntity(merged);
    } catch (error) {
      console.error('Failed to load entity tags:', error);
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [instance, accounts, entityType, idsKey, enabled]);

  useEffect(() => { refresh(); }, [refresh]);

  /** Assign a tag to one or more entities (optimistic). `tag` is the full tag object. */
  const assign = useCallback(async (tag, ids) => {
    const idList = (Array.isArray(ids) ? ids : [ids]).map(String);
    setTagsByEntity((prev) => {
      const next = { ...prev };
      for (const id of idList) {
        const existing = next[id] || [];
        if (!existing.some((t) => t.id === tag.id)) next[id] = [...existing, tag];
      }
      return next;
    });
    try {
      await assignTag(instance, accounts, {
        tagId: tag.id, entityType, entityIds: idList, projectId,
      });
    } catch (error) {
      console.error('Failed to assign tag:', error);
      refresh();
      throw error;
    }
  }, [instance, accounts, entityType, projectId, refresh]);

  /** Remove a tag from one or more entities (optimistic). */
  const unassign = useCallback(async (tag, ids) => {
    const idList = (Array.isArray(ids) ? ids : [ids]).map(String);
    setTagsByEntity((prev) => {
      const next = { ...prev };
      for (const id of idList) {
        if (next[id]) next[id] = next[id].filter((t) => t.id !== tag.id);
      }
      return next;
    });
    try {
      await unassignTag(instance, accounts, { tagId: tag.id, entityType, entityIds: idList });
    } catch (error) {
      console.error('Failed to remove tag:', error);
      refresh();
      throw error;
    }
  }, [instance, accounts, entityType, refresh]);

  return { tagsByEntity, loading, refresh, assign, unassign };
}
